import {
  ButtonMain,
  Pagination,
  Table,
  Loader,
} from '@quero-delivery/quero-components-web';
import { useState } from 'react';
import { Modal } from '@querodelivery/uni-react';
import { useMediaQuery } from 'react-responsive';
import styles from './places-infos-dashboard-modal.module.scss';
import { PlaceRow } from '../place-row';
import { useGetPlacesInfosDashboard } from '../../api/get-places-infos-dashboard';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { setSelectedPlaceToViewRules } from '@/_store/modules/places';
import { useGetPlaceDistributionRules } from '../../api/get-place-distribution-rules';
import { convertMetersToKilometers } from '@/Utils/FnUtils';
import { MODAL_WIDTH } from '@/_constants/styles';

interface PlacesInfosDashboardModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
}

function PlacesInfosDashboardModal({
  open,
  onClose,
}: PlacesInfosDashboardModalProps) {
  const dispatch = useAppDispatch();
  const isTablet = useMediaQuery({ maxWidth: 768 });

  const [page, setPage] = useState(1);

  const focusedPlace = useAppSelector(
    state => state.places.placeSelectedToViewRules,
  );
  const selectedCityOnDashboard = useAppSelector(
    state => state.filters.cityIdOnDashboard,
  );

  const limit = DEFAULT_LIMIT_REQUEST;

  const { data: placesOpen = { data: [], totalCount: 0 }, isLoading } =
    useGetPlacesInfosDashboard(
      {
        cityId: selectedCityOnDashboard || '',
        page,
        limit,
      },
      {
        enabled: !!selectedCityOnDashboard,
      },
    );

  const { data: placeInfo, isLoading: loadingPlaceInfos } =
    useGetPlaceDistributionRules(
      { placeId: focusedPlace?.placeId || '' },
      { enabled: !!focusedPlace },
    );

  const cleanFocusedPlace = () => {
    dispatch(setSelectedPlaceToViewRules(null));
  };

  const hasDeliverymen =
    !!placeInfo?.deliverymenAvailable &&
    !!placeInfo?.deliverymenAvailable.length &&
    placeInfo?.deliverymenAvailable.length > 0;

  const deliverymenAvailableLabel = (() => {
    const label = hasDeliverymen
      ? placeInfo.deliverymenAvailable.length > 1
        ? `${placeInfo?.deliverymenAvailable.length} entregadores`
        : `${placeInfo?.deliverymenAvailable.length} entregador`
      : '';

    return label;
  })();

  const closeModal = () => {
    if (focusedPlace) {
      cleanFocusedPlace();
      onClose(false);
      return;
    }
    onClose(false);
  };

  const maxDeliveriesPerCourier = placeInfo?.maxQuantityDeliveryGroup
    ? placeInfo.maxQuantityDeliveryGroup > 1
      ? `${placeInfo.maxQuantityDeliveryGroup} entregas`
      : `${placeInfo.maxQuantityDeliveryGroup} entrega`
    : '- - -';

  const maxDistanceBetweenDeliveries = placeInfo?.maxDistanceDropsToGroup
    ? convertMetersToKilometers(
        placeInfo.maxDistanceDropsToGroup,
      ).toLocaleString('pt-BR')
    : '- - -';

  const distanceRadius = placeInfo?.maxDistaceRadius
    ? convertMetersToKilometers(placeInfo.maxDistaceRadius).toLocaleString(
        'pt-BR',
      )
    : '- - -';

  return (
    <Modal
      isOpen={open}
      onClose={closeModal}
      className={styles.modal}
      variant={isTablet ? 'bottom' : 'floating'}
      header={
        focusedPlace
          ? 'Configuração de distribuição do estabelecimento'
          : 'Estabelecimentos abertos'
      }
      width={isTablet ? MODAL_WIDTH.xl : MODAL_WIDTH.lg}
    >
      {focusedPlace ? (
        <>
          <span>{deliverymenAvailableLabel}</span>
          <div>
            <div className={styles.rulesDistributionContainer}>
              <div className={styles.rulesDistributionLeftContainer}>
                <h4>Nome do estabelecimento:</h4>
                <div className={styles.placeContainer}>
                  <img
                    src={focusedPlace?.placeAvatarSrc}
                    height={20}
                    alt="iconStatus"
                  />
                  <span>{focusedPlace?.placeName || '- - -'}</span>
                </div>
                <h4>Regras de distribuição:</h4>
                <div className={styles.rulesDistributionTable}>
                  <tr>
                    <span>Raio de entrega</span>
                    <span>{distanceRadius} km</span>
                  </tr>
                  <tr>
                    <span>Distância limite entre entregas</span>
                    <span>{maxDistanceBetweenDeliveries || '- - - '} km</span>
                  </tr>
                  <tr>
                    <span>Quantidade de entregas por entregador</span>
                    <span>{maxDeliveriesPerCourier}</span>
                  </tr>
                  <tr>
                    <span>Pedido dedicado</span>
                    <span>
                      {placeInfo?.hasOnlyDeliverymenDedicated ? 'Sim' : 'Não'}
                    </span>
                  </tr>
                </div>
              </div>
              <div className={styles.rulesDistributionRightContainer}>
                <h4>Entregadores:</h4>
                {loadingPlaceInfos ? (
                  <div className={styles.loaderContainer}>
                    <Loader size="md" />
                  </div>
                ) : (
                  <div className={styles.listContaienr}>
                    {hasDeliverymen ? (
                      placeInfo?.deliverymenAvailable?.map(deliverymanName => (
                        <span key={deliverymanName._id}>
                          {deliverymanName.nome}
                        </span>
                      ))
                    ) : (
                      <span className={styles.noDeliverymenLabel}>
                        Nenhum entregador disponível
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <ButtonMain
              className={styles.closeButton}
              onClick={cleanFocusedPlace}
            >
              Voltar
            </ButtonMain>
          </div>
        </>
      ) : (
        <>
          <span>
            {placesOpen.totalCount}{' '}
            {placesOpen.totalCount > 1
              ? 'Estabelecimentos:'
              : 'Estabelecimento:'}
          </span>
          <div>
            <Table
              className={styles.containerTable}
              pagination={
                <div className={styles.containerButton}>
                  <ButtonMain
                    className={styles.buttonStyle}
                    onClick={() => onClose(false)}
                    width="30%"
                  >
                    Fechar
                  </ButtonMain>
                  <div className="pagination">
                    <Pagination
                      size="sm"
                      limitPerPage={limit}
                      currentPage={page}
                      totalCount={placesOpen.totalCount}
                      onPageChange={setPage}
                    />
                  </div>
                </div>
              }
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Nome do estabelecimento</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">
                    Pedidos em andamento
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">
                    Pedidos com chamado do log
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">
                    Entregadores disponíveis
                  </Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {isLoading ? (
                  <Loader size="md" />
                ) : (
                  placesOpen.data?.map(place => (
                    <PlaceRow place={place} key={place._id} />
                  ))
                )}
              </Table.Body>
            </Table>
          </div>
        </>
      )}
    </Modal>
  );
}

export { PlacesInfosDashboardModal };
